import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

// ─── Types ────────────────────────────────────────────────────────────────────
interface MeterInfo {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
  status: "online" | "offline" | "fault";
  valve_open: boolean;
  mqtt_topic: string;
}
interface TokenBalance {
  meter: number;
  balance: string;
  unit_label: string;
  updated_at: string;
}
interface Property {
  id: number;
  name: string;
  token_value: string; // KES per unit
  unit_label: string;
}
interface PaymentRecord {
  id: number;
  amount: string;
  phone_number: string;
  status: string;
  mpesa_receipt_number: string;
  token_code: string | null;
  units_allocated: string | null;
  created_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PRESETS = [
  { kes: 50,   label: "KES 50",    popular: false },
  { kes: 100,  label: "KES 100",   popular: false },
  { kes: 200,  label: "KES 200",   popular: true  },
  { kes: 500,  label: "KES 500",   popular: false },
  { kes: 1000, label: "KES 1,000", popular: false },
  { kes: 2000, label: "KES 2,000", popular: false },
];

const STATUS_POLL_INTERVAL = 4000; // ms
const MAX_POLL_ATTEMPTS    = 20;   // 80 s total before giving up

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number, d = 1) =>
  new Intl.NumberFormat("en-KE", { maximumFractionDigits: d }).format(n);

function formatPhone(raw: string): string {
  const stripped = raw.replace(/\D/g, "");
  if (stripped.startsWith("0") && stripped.length === 10)
    return "254" + stripped.slice(1);
  if (stripped.startsWith("254") && stripped.length === 12)
    return stripped;
  return stripped;
}

function displayPhone(raw: string): string {
  const f = formatPhone(raw);
  if (f.startsWith("254") && f.length === 12)
    return "+254 " + f.slice(3, 6) + " " + f.slice(6, 9) + " " + f.slice(9);
  return raw;
}

// ─── Step indicator ───────────────────────────────────────────────────────────
type StepState = "idle" | "active" | "done";
function StepBar({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center justify-between w-full px-2 mb-8">
      {steps.map((label, i) => {
        const state: StepState = i < current ? "done" : i === current ? "active" : "idle";
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1.5 min-w-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-black transition-all duration-300 shadow-sm
                ${state === "done"   ? "bg-emerald-500 text-white shadow-emerald-200"
                : state === "active" ? "bg-[#0b1f4a] text-white shadow-slate-300"
                                     : "bg-gray-100 text-gray-400"}`}>
                {state === "done" ? (
                  <svg viewBox="0 0 12 12" fill="none" className="w-4 h-4">
                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : i + 1}
              </div>
              <span className={`text-[10.5px] font-bold whitespace-nowrap
                ${state === "done" ? "text-emerald-600" : state === "active" ? "text-[#0b1f4a]" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 rounded-full transition-colors duration-500 ${i < current ? "bg-emerald-400" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Balance chip ─────────────────────────────────────────────────────────────
function BalanceChip({ balance, label }: { balance: number; label: string }) {
  const state = balance < 5 ? "critical" : balance < 20 ? "low" : "healthy";
  const cfg = {
    critical: "bg-rose-50 border-rose-200 text-rose-700",
    low:      "bg-amber-50 border-amber-200 text-amber-700",
    healthy:  "bg-emerald-50 border-emerald-200 text-emerald-700",
  }[state];
  return (
    <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-[12.5px] font-black ${cfg}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
        <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" />
      </svg>
      {fmt(balance, 1)} {label} remaining
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner({ size = 5, color = "text-white" }: { size?: number; color?: string }) {
  return (
    <svg className={`animate-spin w-${size} h-${size} ${color}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── Payment history row ──────────────────────────────────────────────────────
const PAY_STATUS: Record<string, string> = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  failed:    "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

function PayRow({ p }: { p: PaymentRecord }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-gray-50/70 transition-colors text-left"
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${p.status === "completed" ? "bg-emerald-50" : "bg-gray-100"}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke={p.status === "completed" ? "#10b981" : "#9ca3af"} strokeWidth={1.8} className="w-4.5 h-4.5">
            <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-black text-gray-800">KES {parseFloat(p.amount).toLocaleString()}</p>
          <p className="text-[11px] text-gray-400">{new Date(p.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })}</p>
        </div>
        <span className={`text-[10.5px] font-black px-2.5 py-1 rounded-full border capitalize flex-shrink-0 ${PAY_STATUS[p.status] ?? "bg-gray-100 text-gray-500 border-gray-200"}`}>
          {p.status}
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2.5} className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3.5 bg-gray-50/40 grid grid-cols-2 gap-3">
          {[
            { label: "Phone",   value: displayPhone(p.phone_number) },
            { label: "Receipt", value: p.mpesa_receipt_number || "—" },
            { label: "Units",   value: p.units_allocated ? `${fmt(parseFloat(p.units_allocated), 2)} units` : "—" },
            { label: "Token",   value: p.token_code || "—" },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
              <p className="text-[12.5px] font-bold text-gray-700 font-mono break-all">{value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function BuyTokensPage() {
  const { user } = useAuthStore();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [meter,    setMeter]    = useState<MeterInfo | null>(null);
  const [balance,  setBalance]  = useState<TokenBalance | null>(null);
  const [property, setProperty] = useState<Property | null>(null);
  const [history,  setHistory]  = useState<PaymentRecord[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // ── Purchase flow state ─────────────────────────────────────────────────────
  // step 0 = select amount  |  1 = enter phone  |  2 = processing  |  3 = result
  const [step,        setStep]        = useState(0);
  const [amount,      setAmount]      = useState<number | "">("");
  const [customAmt,   setCustomAmt]   = useState("");
  const [phone,       setPhone]       = useState(user?.phone_number ?? "");
  const [phoneErr,    setPhoneErr]    = useState("");
  const [paymentId,   setPaymentId]   = useState<number | null>(null);
  const [checkoutId,  setCheckoutId]  = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [pollStatus,  setPollStatus]  = useState<"pending"|"completed"|"failed"|"cancelled">("pending");
  const [pollMsg,     setPollMsg]     = useState("Waiting for M-Pesa confirmation…");
  const [receipt,     setReceipt]     = useState("");
  const [tokenCode,   setTokenCode]   = useState("");
  const [unitsGiven,  setUnitsGiven]  = useState("");
  const [simulating,  setSimulating]  = useState(false);

  const pollRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCount  = useRef(0);

  // ── Load meter + balance + property + history ───────────────────────────────
  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/meters/my/").catch(() => ({ data: null })),
      api.get("/tokens/balances/").catch(() => ({ data: [] })),
      api.get("/properties/").catch(() => ({ data: [] })),
      api.get("/payments/history/").catch(() => ({ data: [] })),
    ]).then(([m, b, props, pays]) => {
      const mData: MeterInfo | null = m.data;
      setMeter(mData);

      const bList = b.data.results ?? b.data ?? [];
      if (mData && bList.length > 0) {
        const bal = bList.find((x: TokenBalance) => x.meter === mData.id) ?? bList[0];
        setBalance(bal);
      }

      const pList = props.data.results ?? props.data ?? [];
      if (pList.length > 0) setProperty(pList[0]);

      const payList = pays.data.results ?? pays.data ?? [];
      setHistory(payList);

      // Pre-fill phone from user profile
      if (!phone && user?.phone_number) setPhone(user.phone_number);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { reload(); }, [reload]);

  // ── Stop polling ─────────────────────────────────────────────────────────────
  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    pollCount.current = 0;
  }, []);

  // ── Poll payment status ───────────────────────────────────────────────────────
  const startPolling = useCallback((pid: number, cid: string) => {
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/mpesa/query/${cid}/`);
        const localStatus: string = data.local_status;

        if (localStatus === "completed") {
          stopPoll();
          setPollStatus("completed");
          setPollMsg("Payment confirmed! Your water tokens are being allocated.");
          setReceipt(data.mpesa_receipt ?? "");
          // fetch updated history and balance
          setTimeout(() => {
            reload();
            api.get(`/payments/history/`).then(({ data: d }) => {
              const list = d.results ?? d ?? [];
              setHistory(list);
              const found = list.find((p: PaymentRecord) => p.id === pid);
              if (found) {
                setTokenCode(found.token_code ?? "");
                setUnitsGiven(found.units_allocated ?? "");
              }
            });
          }, 2000);
          return;
        }
        if (localStatus === "failed" || localStatus === "cancelled") {
          stopPoll();
          setPollStatus(localStatus as any);
          setPollMsg(
            localStatus === "cancelled"
              ? "Payment was cancelled. You can try again."
              : "Payment failed. Please try again or use a different number."
          );
          return;
        }
        if (pollCount.current >= MAX_POLL_ATTEMPTS) {
          stopPoll();
          setPollMsg("Timed out waiting for M-Pesa. Check your SMS — the payment may still go through.");
        }
      } catch {
        // network hiccup — keep polling
      }
    }, STATUS_POLL_INTERVAL);
  }, [stopPoll, reload]);

  // ── Sandbox simulate ─────────────────────────────────────────────────────────
  const simulateSandbox = useCallback(async () => {
    if (!paymentId) return;
    setSimulating(true);
    try {
      const { data } = await api.post(`/payments/sandbox/simulate/${paymentId}/`);
      stopPoll();
      setPollStatus("completed");
      setPollMsg("Sandbox payment simulated successfully! Tokens are being allocated…");
      setReceipt(data.simulated_receipt ?? "");
      setTimeout(() => {
        reload();
        api.get("/payments/history/").then(({ data: d }) => {
          const list = d.results ?? d ?? [];
          setHistory(list);
          const found = list.find((p: PaymentRecord) => p.id === paymentId);
          if (found) {
            setTokenCode(found.token_code ?? "");
            setUnitsGiven(found.units_allocated ?? "");
          }
        });
      }, 1500);
    } catch (e: any) {
      alert(e?.response?.data?.detail ?? "Simulation failed — check server logs.");
    } finally {
      setSimulating(false);
    }
  }, [paymentId, stopPoll, reload]);

  // ── Submit STK push ──────────────────────────────────────────────────────────
  const submitPayment = async () => {
    if (!meter || !amount) return;
    const formatted = formatPhone(phone);
    if (!/^2547\d{8}$/.test(formatted)) {
      setPhoneErr("Enter a valid Kenyan Safaricom/Airtel number (e.g. 0712 345 678)");
      return;
    }
    setPhoneErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/initiate/", {
        meter_id: meter.id,
        amount: Number(amount),
        phone_number: formatted,
      });
      setPaymentId(data.payment_id);
      setCheckoutId(data.checkout_request_id ?? "");
      setStep(2);
      setPollStatus("pending");
      setPollMsg("STK push sent! Check your phone and enter your M-Pesa PIN to pay…");
      startPolling(data.payment_id, data.checkout_request_id);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? "Failed to initiate payment. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cleanup on unmount ───────────────────────────────────────────────────────
  useEffect(() => () => stopPoll(), [stopPoll]);

  // ── Reset flow ───────────────────────────────────────────────────────────────
  const reset = () => {
    stopPoll();
    setStep(0); setAmount(""); setCustomAmt(""); setPhoneErr("");
    setPaymentId(null); setCheckoutId(""); setPollStatus("pending");
    setReceipt(""); setTokenCode(""); setUnitsGiven(""); setError(null);
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const tokenValue   = property?.token_value ? parseFloat(property.token_value) : 1;
  const unitLabel    = property?.unit_label ?? balance?.unit_label ?? "litres";
  const unitsPreview = amount ? (Number(amount) / tokenValue) : 0;
  const balanceNum   = balance ? parseFloat(balance.balance) : 0;
  const effectiveAmt = amount !== "" ? Number(amount) : 0;

  // ─── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-5 pt-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-16 pt-2 space-y-6">

      {/* ── Page header ── */}
      <div>
        <h1 className="text-[22px] font-black text-gray-900">Buy Water Tokens</h1>
        <p className="text-[13px] text-gray-400 mt-0.5">
          Purchase M-Pesa tokens to top up your water balance and keep the flow going.
        </p>
      </div>

      {/* ── Meter + balance hero ── */}
      {meter ? (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#07183a] via-[#0d2660] to-[#152f7a] p-5 shadow-xl">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-blue-400/10 blur-3xl" />
            <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
              <defs><pattern id="d" width="24" height="24" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="white"/></pattern></defs>
              <rect width="100%" height="100%" fill="url(#d)"/>
            </svg>
          </div>
          <div className="relative z-10 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-black text-sky-400/60 uppercase tracking-[0.2em] mb-1">Smart Meter</p>
              <p className="text-[17px] font-black text-white leading-tight">{meter.property_name}</p>
              <p className="text-[12.5px] text-sky-300/50 mt-0.5">
                Unit <span className="text-sky-200 font-bold">{meter.unit_number}</span>
                <span className="mx-2 text-white/20">·</span>
                <span className="font-mono text-[11.5px]">{meter.serial_number}</span>
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full
                  ${meter.status === "online" ? "bg-emerald-400/20 text-emerald-300 border border-emerald-400/30" : "bg-gray-400/20 text-gray-300 border border-gray-400/30"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meter.status === "online" ? "bg-emerald-400 animate-pulse" : "bg-gray-400"}`} />
                  {meter.status}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full
                  ${meter.valve_open ? "bg-sky-400/20 text-sky-300 border border-sky-400/30" : "bg-rose-400/20 text-rose-300 border border-rose-400/30"}`}>
                  Valve {meter.valve_open ? "Open" : "Closed"}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold mb-1">Balance</p>
              <p className={`text-[36px] font-black leading-none ${balanceNum < 5 ? "text-rose-400" : balanceNum < 20 ? "text-amber-400" : "text-emerald-400"}`}>
                {fmt(balanceNum, 1)}
              </p>
              <p className="text-[11px] text-white/30 mt-1">{unitLabel}</p>
              {balanceNum < 20 && (
                <p className={`text-[10.5px] font-black mt-1 ${balanceNum < 5 ? "text-rose-400" : "text-amber-400"}`}>
                  {balanceNum < 5 ? "🚨 Critical — top up now!" : "⚠ Running low"}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 p-10 text-center">
          <p className="text-[14px] font-black text-gray-600">No meter assigned</p>
          <p className="text-[12.5px] text-gray-400 mt-1">Contact your property manager to assign a meter first.</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200">
          <svg viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth={2} className="w-5 h-5 flex-shrink-0 mt-0.5">
            <circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01" strokeLinecap="round"/>
          </svg>
          <p className="text-[13px] text-rose-700 font-semibold">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-rose-400 hover:text-rose-600">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Purchase flow ── */}
      {meter && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 pb-4 border-b border-gray-100">
            <StepBar steps={["Select Amount", "Confirm & Pay", "Processing", "Done"]} current={step} />
          </div>

          {/* ── Step 0: Select amount ── */}
          {step === 0 && (
            <div className="p-6 space-y-5">
              <div>
                <p className="text-[12px] font-black text-gray-400 uppercase tracking-wider mb-3">
                  Select a package
                  {tokenValue > 0 && (
                    <span className="ml-2 font-normal text-gray-400 normal-case">
                      · KES {fmt(tokenValue, 2)} per {unitLabel}
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {PRESETS.map(({ kes, label, popular }) => {
                    const units = kes / tokenValue;
                    const selected = amount === kes;
                    return (
                      <button
                        key={kes}
                        onClick={() => { setAmount(kes); setCustomAmt(""); }}
                        className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-200
                          ${selected
                            ? "border-[#0b1f4a] bg-[#0b1f4a] shadow-lg shadow-slate-300/50 -translate-y-0.5"
                            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        {popular && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black px-2 py-0.5 bg-sky-500 text-white rounded-full whitespace-nowrap">
                            POPULAR
                          </span>
                        )}
                        <p className={`text-[15px] font-black ${selected ? "text-white" : "text-gray-800"}`}>{label}</p>
                        <p className={`text-[11px] mt-0.5 ${selected ? "text-white/60" : "text-gray-400"}`}>
                          {fmt(units, 1)} {unitLabel}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom amount */}
              <div>
                <p className="text-[12px] font-black text-gray-400 uppercase tracking-wider mb-2">Or enter a custom amount</p>
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-2 flex-1 border-2 rounded-xl px-4 py-2.5 transition-all
                    ${customAmt ? "border-[#0b1f4a] bg-white" : "border-gray-200 bg-gray-50"}`}>
                    <span className="text-[13px] font-black text-gray-400">KES</span>
                    <input
                      type="number"
                      min={10}
                      placeholder="e.g. 350"
                      value={customAmt}
                      onChange={(e) => {
                        setCustomAmt(e.target.value);
                        const n = parseFloat(e.target.value);
                        if (!isNaN(n) && n > 0) setAmount(n);
                        else setAmount("");
                      }}
                      className="flex-1 bg-transparent outline-none text-[15px] font-black text-gray-800 placeholder:text-gray-300"
                    />
                  </div>
                  {customAmt && parseFloat(customAmt) > 0 && (
                    <div className="text-right min-w-[90px]">
                      <p className="text-[12px] text-gray-400">You get</p>
                      <p className="text-[14px] font-black text-teal-600">{fmt(parseFloat(customAmt) / tokenValue, 2)} {unitLabel}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview */}
              {amount !== "" && effectiveAmt > 0 && (
                <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                  <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} className="w-5 h-5">
                      <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-[13px] font-black text-sky-800">
                      KES {fmt(effectiveAmt, 0)} → {fmt(unitsPreview, 2)} {unitLabel}
                    </p>
                    <p className="text-[11.5px] text-sky-600 mt-0.5">
                      New balance after purchase: <span className="font-black">{fmt(balanceNum + unitsPreview, 2)} {unitLabel}</span>
                    </p>
                  </div>
                </div>
              )}

              <button
                disabled={!amount || effectiveAmt < 10}
                onClick={() => setStep(1)}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#0b1f4a] to-[#1a3a8a] text-white text-[14px] font-black shadow-lg shadow-slate-300/40 hover:shadow-slate-300/60 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none"
              >
                Continue to Payment →
              </button>
            </div>
          )}

          {/* ── Step 1: Phone + confirm ── */}
          {step === 1 && (
            <div className="p-6 space-y-5">
              {/* Summary */}
              <div className="flex items-start justify-between p-4 rounded-xl bg-gray-50 border border-gray-200">
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wider font-black mb-1">You are paying</p>
                  <p className="text-[28px] font-black text-[#0b1f4a] leading-none">KES {fmt(effectiveAmt, 0)}</p>
                  <p className="text-[12px] text-gray-500 mt-1">
                    → <span className="font-black text-teal-600">{fmt(unitsPreview, 2)} {unitLabel}</span> of water
                  </p>
                </div>
                <button onClick={() => setStep(0)} className="text-[12px] font-black text-sky-500 hover:text-sky-700 transition-colors">
                  Change
                </button>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[12px] font-black text-gray-500 uppercase tracking-wider mb-2">
                  M-Pesa phone number
                </label>
                <div className={`flex items-center gap-2 border-2 rounded-xl px-4 py-3 transition-all
                  ${phoneErr ? "border-rose-400 bg-rose-50" : "border-gray-200 focus-within:border-[#0b1f4a] bg-white"}`}>
                  <span className="text-[13px] font-bold text-gray-400">🇰🇪 +254</span>
                  <input
                    type="tel"
                    placeholder="7XX XXX XXX"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneErr(""); }}
                    className="flex-1 bg-transparent outline-none text-[15px] font-bold text-gray-800 placeholder:text-gray-300"
                  />
                </div>
                {phoneErr && <p className="text-[11.5px] text-rose-600 mt-1.5 font-semibold">{phoneErr}</p>}
                <p className="text-[11.5px] text-gray-400 mt-1.5">
                  You will receive an STK push prompt on this number. Enter your M-Pesa PIN to confirm.
                </p>
              </div>

              {/* Meter */}
              <div className="flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-200">
                <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.8} className="w-5 h-5 flex-shrink-0">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 12h8M12 8v8" strokeLinecap="round"/>
                </svg>
                <div>
                  <p className="text-[12px] font-black text-gray-700">{meter.serial_number}</p>
                  <p className="text-[11px] text-gray-400">{meter.property_name} · Unit {meter.unit_number}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(0)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-[13px] font-black text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={submitPayment}
                  disabled={submitting}
                  className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[14px] font-black shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Spinner /><span>Sending…</span></> : "Pay KES " + fmt(effectiveAmt, 0) + " via M-Pesa"}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Processing ── */}
          {step === 2 && (
            <div className="p-8 flex flex-col items-center text-center gap-6">
              {pollStatus === "pending" && (
                <>
                  {/* Animated phone prompt illustration */}
                  <div className="relative w-24 h-24">
                    <div className="absolute inset-0 rounded-full bg-sky-100 animate-ping opacity-40" />
                    <div className="absolute inset-2 rounded-full bg-sky-200 animate-ping opacity-40" style={{ animationDelay: "0.3s" }} />
                    <div className="relative z-10 w-24 h-24 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-xl shadow-sky-300">
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-10 h-10">
                        <rect x="5" y="2" width="14" height="20" rx="2"/>
                        <path d="M12 18h.01" strokeWidth={2.5} strokeLinecap="round"/>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <p className="text-[17px] font-black text-gray-800">Check Your Phone</p>
                    <p className="text-[13px] text-gray-500 mt-1.5 max-w-xs mx-auto">{pollMsg}</p>
                    <p className="text-[12px] text-gray-400 mt-1">
                      Paying <span className="font-black text-gray-700">KES {fmt(effectiveAmt, 0)}</span> from{" "}
                      <span className="font-mono font-black text-gray-700">{displayPhone(phone)}</span>
                    </p>
                  </div>

                  {/* Sandbox simulate button */}
                  <div className="w-full p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50">
                    <p className="text-[11.5px] font-black text-amber-700 uppercase tracking-wider mb-2">🧪 Sandbox / Testing Mode</p>
                    <p className="text-[12px] text-amber-600 mb-3">
                      Since we're using sandbox credentials without a real till number, click below to simulate a successful payment.
                    </p>
                    <button
                      onClick={simulateSandbox}
                      disabled={simulating}
                      className="w-full py-2.5 rounded-xl bg-amber-500 text-white text-[13px] font-black hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {simulating ? <><Spinner color="text-white" size={4}/><span>Simulating…</span></> : "✅ Simulate Successful Payment"}
                    </button>
                  </div>

                  <button
                    onClick={() => { stopPoll(); reset(); }}
                    className="text-[12.5px] text-gray-400 hover:text-gray-600 transition-colors font-semibold"
                  >
                    Cancel & start over
                  </button>
                </>
              )}

              {pollStatus === "completed" && (
                <>
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl shadow-emerald-200">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} className="w-11 h-11">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-[20px] font-black text-gray-900">Payment Successful! 🎉</p>
                    <p className="text-[13px] text-gray-500 mt-1.5 max-w-xs mx-auto">{pollMsg}</p>
                  </div>
                  <div className="w-full space-y-2.5">
                    {[
                      { label: "Amount Paid",   value: `KES ${fmt(effectiveAmt, 0)}` },
                      { label: "Units Added",   value: unitsGiven ? `${fmt(parseFloat(unitsGiven), 2)} ${unitLabel}` : `~${fmt(unitsPreview, 2)} ${unitLabel}` },
                      { label: "New Balance",   value: `${fmt(balanceNum + unitsPreview, 2)} ${unitLabel}` },
                      { label: "Receipt",       value: receipt || "Processing…" },
                      { label: "Token Code",    value: tokenCode || "Being generated…" },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-100">
                        <p className="text-[12px] font-black text-gray-400 uppercase tracking-wider">{label}</p>
                        <p className="text-[13px] font-black text-gray-800 font-mono">{value}</p>
                      </div>
                    ))}
                  </div>
                  {!meter.valve_open && (
                    <div className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-sky-50 border border-sky-200">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={2} className="w-5 h-5 flex-shrink-0 mt-0.5">
                        <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3" strokeLinecap="round"/>
                      </svg>
                      <p className="text-[12.5px] text-sky-700 font-semibold">
                        Your valve was closed. It has now been re-opened automatically — water should start flowing shortly.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => { reset(); reload(); setStep(3); }}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#0b1f4a] to-[#1a3a8a] text-white text-[14px] font-black shadow-lg shadow-slate-300/40 hover:shadow-slate-300/60 transition-all hover:-translate-y-0.5"
                  >
                    Done — View History
                  </button>
                </>
              )}

              {(pollStatus === "failed" || pollStatus === "cancelled") && (
                <>
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-400 to-red-500 flex items-center justify-center shadow-xl shadow-rose-200">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} className="w-11 h-11">
                      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-[20px] font-black text-gray-900">
                      {pollStatus === "cancelled" ? "Payment Cancelled" : "Payment Failed"}
                    </p>
                    <p className="text-[13px] text-gray-500 mt-1.5 max-w-xs mx-auto">{pollMsg}</p>
                  </div>
                  <button
                    onClick={reset}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#0b1f4a] to-[#1a3a8a] text-white text-[14px] font-black shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Step 3: View history (after success) ── */}
          {step === 3 && (
            <div className="p-6">
              <button onClick={reset} className="flex items-center gap-2 text-[13px] font-black text-sky-500 hover:text-sky-700 mb-5 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
                  <path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Buy more tokens
              </button>
              <h3 className="text-[14.5px] font-black text-gray-800 mb-1">Payment History</h3>
              <p className="text-[12px] text-gray-400 mb-4">{history.length} transaction{history.length !== 1 ? "s" : ""}</p>
              <div className="space-y-2">
                {history.slice(0, 10).map((p) => <PayRow key={p.id} p={p} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── How it works ── */}
      {step === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-[14px] font-black text-gray-800 mb-4">How It Works</h3>
          <div className="space-y-3.5">
            {[
              { n: "1", title: "Select Amount",      desc: "Choose a preset package or enter a custom KES amount.",    color: "from-sky-500 to-blue-600"    },
              { n: "2", title: "Enter Your Phone",   desc: "Enter the Safaricom/Airtel number to receive the STK push.", color: "from-violet-500 to-purple-700" },
              { n: "3", title: "Confirm on Phone",   desc: "You'll get an M-Pesa prompt. Enter your PIN to pay.",      color: "from-amber-500 to-orange-600"  },
              { n: "4", title: "Tokens Allocated",   desc: "Water units are credited and your valve opens instantly.", color: "from-emerald-500 to-teal-600"  },
            ].map((s) => (
              <div key={s.n} className="flex items-start gap-3.5">
                <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <span className="text-[12px] font-black text-white">{s.n}</span>
                </div>
                <div>
                  <p className="text-[13px] font-black text-gray-800">{s.title}</p>
                  <p className="text-[11.5px] text-gray-400 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment history ── */}
      {history.length > 0 && step === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[14.5px] font-black text-gray-800">Recent Transactions</h3>
              <p className="text-[11.5px] text-gray-400 mt-0.5">Your last {Math.min(history.length, 8)} payments</p>
            </div>
            <BalanceChip balance={balanceNum} label={unitLabel} />
          </div>
          <div className="space-y-2">
            {history.slice(0, 8).map((p) => <PayRow key={p.id} p={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
